import { z } from "zod"

export const CHARGE_CLASSES = [
  `TRANSMISSION`,
  `DISTRIBUTION`,
  `NON_BYPASSABLE`,
  `SUPPLY`,
  `TAX`,
  `OTHER`,
  `CONTRACTED`,
  `USER_ADJUSTED`,
  `AFTER_TAX`,
] as const
export const chargeClassesSchema = z.array(z.enum(CHARGE_CLASSES))
/**
 * Represents a calculated cost item.
 */
export const calculatedCostItemSchema = z.object({
  /**
   * Unique Genability ID for the tariff associated with this cost item.
   */
  tariffId: z.number().optional().nullable(),

  /**
   * Unique Genability ID for the tariff rate associated with this cost item.
   */
  tariffRateId: z.number().optional().nullable(),

  /**
   * Unique Genability ID for the band associated with this cost item.
   */
  tariffRateBandId: z.number().optional().nullable(),

  /**
   * The sequence number of the rate group that the tariff rate belongs to.
   */
  rateSequenceNumber: z.number().optional().nullable(),

  /**
   * The name of the group this rate belongs to.
   */
  rateGroupName: z.string().optional().nullable(),

  /**
   * The name of this rate.
   */
  rateName: z.string().optional().nullable(),

  /**
   * The start date and time of the period applicable for this cost item.
   */
  fromDateTime: z.string(),

  /**
   * The end date and time of the period applicable for this cost item.
   */
  toDateTime: z.string(),

  /**
   * The key for the quantity this calculated cost item refers to.
   * Possible values include: fixed, consumption, minimum, and demand.
   */
  quantityKey: z.string(),

  /**
   * The textual description of the quantity key specified by quantityKey.
   */
  quantityKeyDescription: z.string().optional().nullable(),

  /**
   * The type of rate this charge is.
   * Current values include COST_PER_UNIT, which is a cost
   * for each unit of quantity consumed (e.g. $0.10 per kWh),
   * and PERCENTAGE, which is a percent of another cost (e.g. 10% of your bill).
   * BLOCK and BLOCK_SELL_BACK are also supported. See Block and Index Rates.
   */
  rateType: z.string().optional().nullable(),

  /**
   * The monetary amount of the rate associated with this cost item.
   */
  rateAmount: z.number(),

  /**
   * For a tiered rate, the minimum quantity needed to invoke this tier.
   */
  tierLowerLimit: z.number().optional().nullable(),

  /**
   * For a tiered rate, the maximum quantity at which this tier is invoked.
   */
  tierUpperLimit: z.number().optional().nullable(),

  /**
   * Total quantity used for this item's charge.
   * This will typically be 1 but will have different
   * values for charges related to the number of units
   * of some items (e.g. number of billing meters at a facility).
   */
  itemQuantity: z.number(),

  /**
   * This is the total cost for this line item.
   */
  cost: z.number().nullable().optional(),

  /**
   * The amount this rate is being prorated compared to the full rate amount.
   */
  rateProration: z.number().optional().nullable(),

  /**
   * The type of rate: CONSUMPTION_BASED, DEMAND_BASED,
   * FIXED_TYPE, QUANTITY, MINIMUM, TAX, or NET_EXCESS_GENERATION.
   */
  chargeType: z.string().optional().nullable(),

  /**
   * The class of rate: TRANSMISSION, DISTRIBUTION, SUPPLY, NON_BYPASSABLE.
   * TAX, OTHER, CONTRACTED, USER_ADJUSTED, or AFTER_TAX.
   */
  chargeClass: z
    .string()
    // .refine(
    //   (string) => {
    //     if (string) {
    //       const arr = string.split(`,`)
    //       return chargeClassesSchema.safeParse(arr).success
    //     }
    //     return string === undefined
    //   },
    //   {
    //     message: `chargeClass must be a comma separated
    // list made up of ${(() => {
    //       const arr = CHARGE_CLASSES.map(
    //         (c, i) => `"${c}"${i === CHARGE_CLASSES.length - 1 ? `` : `, `}`,
    //       )
    //       const or = `or `
    //       arr.splice(arr.length - 1, 0, or)
    //       return arr.join(``)
    //     })()}`,
    //   },
    // )
    .optional()
    .nullable(),

  /**
   * The Time of Use type: OFF_PEAK, PARTIAL_PEAK, ON_PEAK, or CRITICAL_PEAK.
   */
  period: z.string().optional().nullable(),

  /**
   * For demand charges, the time from which demand is measured.
   */
  demandInterval: z.string().optional().nullable(),

  /**
   * The duration in milliseconds (Optional).
   */
  duration: z.number().optional().nullable(),

  /**
   * The unique Genability ID of the time of use associated
   * with this cost item. (Optional)
   */
  touId: z.number().optional().nullable(),

  /**
   * Human-readable name of the time of use specified by touId. (Optional)
   */
  touName: z.string().optional().nullable(),

  /**
   * The unique Genability ID of the season in which the
   * time of use period is situated, which can affect the rate. (Optional)
   */
  seasonId: z.number().optional().nullable(),

  /**
   * Human-readable name for the season in which the time of
   * use period is situated. (Optional)
   */
  seasonName: z.string().optional().nullable(),

  /**
   * The formula associated with the quantityKey.
   */
  formula: z.string().optional().nullable(),

  /**
   * Transaction type categories: BUY, SELL, NET, BUY_IMPORT, or SELL_EXPORT.
   */
  transactionType: z.string().optional().nullable(),
})

export type CalculatedCostItem = z.infer<typeof calculatedCostItemSchema>
